import React from "react";
import { Link as RouterLink } from "react-router";
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  Grid,
  Paper,
  Chip
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import VerifiedIcon from "@mui/icons-material/Verified";
import hero_img from "../assets/mining-platform-benefits.jpg";

const titleDividerSx = { width: 48, height: 2, backgroundColor: "primary.main", mt: 1.5, mb: 3 };
const titleSx = {
  fontWeight: 800,
  lineHeight: 1.14,
  letterSpacing: "-0.3px",
  mb: 1,
  fontSize: { xs: "1.85rem", md: "2.3rem" },
};
const bodySx = {
  color: "text.secondary",
  lineHeight: 1.75,
  fontSize: { xs: "1rem", md: "1.05rem" },
};
const cardTitleSx = { fontWeight: 700, lineHeight: 1.3 };
const cardBodySx = { color: "text.secondary", lineHeight: 1.7 };

const currentBenefits = [
  {
    title: "PR amplification",
    description:
      "When you publish an important update, ResourceNAV can amplify it through featured placements such as the home page, social distribution, and release-style announcements - especially when you rank in top scoring categories.",
  },
  {
    title: "Credibility badges",
    description:
      "Earn visible trust signals like “Claimed Profile”, “Verified Data”, and “High Confidence Data” so investors know your information is sourced and maintained by the company.",
  },
  {
    title: "Verified thematic watchlists",
    description:
      "Be included in curated watchlists that focus on claimed and verified profiles—helping investors discover companies with higher-quality data.",
  },
  {
    title: "Company narrative + context",
    description:
      "Add context behind numbers so investors understand the “why” (e.g., “Permitting delayed due to community consultation, not regulatory rejection.”).",
  },
];

const futureBenefits = [
  {
    title: "Signals",
    description:
      "System-generated signals highlight meaningful events (major updates, milestones, notable changes). Claimed profiles can unlock additional signal types and investor notification options.",
  },
  {
    title: "Investor Q&A events",
    description:
      "Eligibility for virtual roadshows, thematic investor briefings, and Q&A sessions - reserved for claimed companies.",
  },
  {
    title: "Profile-based investor alerts",
    description:
      "Investors can opt in to notifications when claimed companies update key metrics (resource estimates, permitting status, production changes, etc.).",
  },
  {
    title: "Permitting milestone tracker",
    description:
      "A visual tracker that helps companies map and communicate regulatory progress clearly over time.",
  },
  {
    title: "Data ingestion & quarterly refreshes",
    description:
      "Structured workflows to capture quarterly updates across permitting, production volumes, resources/reserves, and other key disclosures.",
  },
  {
    title: "Page & site analytics",
    description:
      "Aggregated, anonymized insights into what investors view and track - so you understand what the market cares about.",
  },
];

function CTAButtons() {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
      <Button
        variant="contained"
        color="primary"
        component="a"
        href="mailto:resourcenav@naturalresourcestocks.net?subject=Claim%20Company%20Profile"
        sx={{px:5}}
      >
        Contact Sales
      </Button>
      <Button variant="outlined" component={RouterLink} to="/companies" sx={{px:5}}>
        Find Your Company
      </Button>
    </Stack>
  );
}

export default function BenefitsPage() {
  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
              backgroundImage: `linear-gradient(
                rgba(0,0,0,0.35),
                rgba(0,0,0,0.2)
              ),url(${hero_img})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              py: 12,
              px: 3,
              color: "white",
              position: "relative",
            }}
      >
        <Container maxWidth={false} sx={{ position: "relative", zIndex: 1, maxWidth: "lg" }}>
          <Stack spacing={2} maxWidth={900}>
            <Typography variant="h3" fontWeight={800}>
              Turn your public listing into a verified source of truth
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(255,255,255)" }}>
              Claimed profiles help investors trust your data, understand your story, and stay up to date as you progress.
            </Typography>
            <CTAButtons />
            <Typography variant="caption" color="rgba(255,255,255)">
              Claim approval is quick. We verify requests to protect your profile.
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* BODY */}
      <Container maxWidth={false} sx={{ maxWidth: "lg", py: 6, px: { xs: 2, md: 3 } }}>
        {/* Current benefits */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="overline" sx={{ color: "primary.main", letterSpacing: 1.2, textTransform: "uppercase" }}>
            BENEFITS
          </Typography>
          <Typography variant="h5" sx={titleSx}>
            Benefits available now
          </Typography>
          <Box sx={{ ...titleDividerSx, mx: 0 }} />
          <Typography variant="body1" sx={{ ...bodySx }}>
            Once your profile is claimed, you can publish verified updates, add context to metrics, and improve how your
            company appears across ResourceNAV.
          </Typography>
          <Grid container spacing={3} sx={{mt:2}}>
            {currentBenefits.map((item, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={item.title}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    textAlign: "left",
                    borderRadius: 3,
                    height: "100%",
                    borderTop: index === 0 ? "2px solid #d9b352" : undefined,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <VerifiedIcon color="primary" />
                    </Box>
                    <Typography variant="h6" sx={cardTitleSx}>
                      {item.title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={cardBodySx}>
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Future benefits */}
        <Box sx={{ mb: 6, mt: 3 }}>
          <Typography variant="overline" sx={{ color: "primary.main", letterSpacing: 1.2, textTransform: "uppercase" }}>
            THE ROADMAP
          </Typography>
          <Typography variant="h5" sx={titleSx}>
            Coming next on ResourceNAV
          </Typography>
          <Box sx={{ ...titleDividerSx, mx: 0 }} />
          <Typography variant="body1" sx={{ ...bodySx }}>
            We’re building tools that reward transparency and keep investors informed—while giving companies a better way
            to communicate progress.
          </Typography>
          <Grid container spacing={3} sx={{mt:2}}>
            {futureBenefits.map((item, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={item.title}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    textAlign: "left",
                    borderRadius: 3,
                    height: "100%",
                    borderTop: index === 0 ? "2px solid #d9b352" : undefined,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <RocketLaunchIcon color="primary" />
                    </Box>
                    <Typography variant="h6" sx={cardTitleSx}>
                      {item.title}
                    </Typography>
                    <Chip label="Coming soon" size="small" />
                  </Stack>
                  <Typography variant="body2" sx={cardBodySx}>
                    {item.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Closing CTA */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 7, md: 10 },
          px: { xs: 3, md: 6 },
          textAlign: "center",
          width: "100%",
          background: "linear-gradient(120deg, rgba(38,38,38,0.92), rgba(201, 176, 123,0.9))",
          color: "white"
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 20% 20%, rgba(227,154,74,0.18), transparent 35%), radial-gradient(circle at 80% 0%, rgba(67,129,193,0.2), transparent 30%)",
            pointerEvents: "none",
          }}
        />
        <Typography variant="h4" fontWeight={900} sx={{ mb: 2, position: "relative", zIndex: 1 }}>
          Ready to claim your profile?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            maxWidth: 780,
            mx: "auto",
            mb: 3,
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.85)",
            position: "relative",
            zIndex: 1,
          }}
        >
          Claiming is the fastest way to ensure your information stays accurate and investor-ready. If you're unsure where to start, we'll help.
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="center"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Button
            variant="contained"
            color="primary"
            component="a"
            href="mailto:resourcenav@naturalresourcestocks.net?subject=Claim%20Company%20Profile"
            sx={{ px: 5 }}
          >
            Contact Sales
          </Button>
          <Button
            variant="outlined"
            component={RouterLink}
            to="/companies"
            sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", px: 5 }}
          >
            Find Your Company
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
